/**
 * Bottom Sheet Component
 * 
 * Mobile-friendly bottom drawer
 * 
 * Version: 6.2.0
 */
import React, { useEffect, useRef, ReactNode } from 'react'
import './BottomSheet.css'

interface BottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  snapPoints?: number[] // Percentage values for snap points
  initialSnap?: number // Index of initial snap point
  enableDrag?: boolean
  className?: string
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  snapPoints = [50, 90],
  initialSnap = 0,
  enableDrag = true,
  className = ''
}) => {
  const sheetRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const startYRef = useRef(0)
  const currentYRef = useRef(0)
  const isDraggingRef = useRef(false)

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Drag handling
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enableDrag) return
    isDraggingRef.current = true
    startYRef.current = e.touches[0].clientY
    currentYRef.current = startYRef.current
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDraggingRef.current || !enableDrag) return
    
    currentYRef.current = e.touches[0].clientY
    const diff = currentYRef.current - startYRef.current
    
    if (diff > 0) {
      // Only allow dragging down
      if (sheetRef.current) {
        sheetRef.current.style.transform = `translateY(${diff}px)`
        sheetRef.current.style.opacity = `${1 - diff / 300}`
      }
    }
  }

  const handleTouchEnd = () => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false

    const diff = currentYRef.current - startYRef.current
    
    if (diff > 100) {
      onClose()
    } else if (sheetRef.current) {
      sheetRef.current.style.transform = ''
      sheetRef.current.style.opacity = ''
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="bottom-sheet-overlay" onClick={handleOverlayClick}>
      <div 
        ref={sheetRef}
        className={`bottom-sheet ${className}`}
        style={{ '--snap-height': `${snapPoints[initialSnap]}vh` } as React.CSSProperties}
      >
        {/* Drag Handle */}
        <div 
          className="bottom-sheet-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="handle-bar" />
        </div>

        {/* Header */}
        {title && (
          <div className="bottom-sheet-header">
            <h3 className="bottom-sheet-title">{title}</h3>
            <button className="bottom-sheet-close" onClick={onClose}>
              ✕
            </button>
          </div>
        )}

        {/* Content */}
        <div ref={contentRef} className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </div>
  )
}

export default BottomSheet